import { ref, onUnmounted } from 'vue';
import { aiService } from '@/services/AIService';
import type { ModelInvocationStatusResponse } from '@/types/ai';

const pollingIntervals = new Map<string, NodeJS.Timeout>();

// Exponential backoff intervals: 1s, 2s, 4s, 8s, 16s, 30s, 60s, 2min (max)
const BACKOFF_INTERVALS = [1000, 2000, 4000, 8000, 16000, 30000, 60000, 120000];

export function useModelInvocation() {
  const isPolling = ref(false);

  const pollInvocationStatus = (
    invocationId: string,
    onComplete: (result: ModelInvocationStatusResponse) => void,
    onError: (error: Error) => void,
    onStatusUpdate?: (status: ModelInvocationStatusResponse) => void
  ) => {
    if (pollingIntervals.has(invocationId)) {
      // Already polling this invocation
      return;
    }

    isPolling.value = true;
    let attempt = 0;
    let currentInterval = BACKOFF_INTERVALS[0];

    const poll = async () => {
      try {
        const status = await aiService.getInvocationStatus(invocationId);

        // Call status update callback if provided
        if (onStatusUpdate) {
          onStatusUpdate(status);
        }

        // Check if completed or failed
        if (status.status === 'COMPLETED' || status.status === 'FAILED') {
          stopPollingInvocation(invocationId);
          onComplete(status);
          return;
        }

        // Continue polling with exponential backoff
        attempt++;
        const intervalIndex = Math.min(attempt, BACKOFF_INTERVALS.length - 1);
        currentInterval = BACKOFF_INTERVALS[intervalIndex];

        const timeoutId = setTimeout(poll, currentInterval);
        pollingIntervals.set(invocationId, timeoutId);
      } catch (error) {
        // On error, retry with backoff
        attempt++;
        const intervalIndex = Math.min(attempt, BACKOFF_INTERVALS.length - 1);
        currentInterval = BACKOFF_INTERVALS[intervalIndex];

        // If we've tried too many times, stop and call error handler
        if (attempt >= BACKOFF_INTERVALS.length * 2) {
          stopPollingInvocation(invocationId);
          onError(error instanceof Error ? error : new Error('Polling failed'));
          return;
        }

        const timeoutId = setTimeout(poll, currentInterval);
        pollingIntervals.set(invocationId, timeoutId);
      }
    };

    // Start polling immediately
    poll();
  };

  const stopPollingInvocation = (invocationId: string) => {
    const intervalId = pollingIntervals.get(invocationId);
    if (intervalId) {
      clearTimeout(intervalId);
      pollingIntervals.delete(invocationId);
    }

    // Update isPolling if no more active polls
    if (pollingIntervals.size === 0) {
      isPolling.value = false;
    }
  };

  const stopAllPolling = () => {
    pollingIntervals.forEach((intervalId) => {
      clearTimeout(intervalId);
    });
    pollingIntervals.clear();
    isPolling.value = false;
  };

  // Cleanup on unmount
  onUnmounted(() => {
    stopAllPolling();
  });

  return {
    isPolling,
    pollInvocationStatus,
    stopPollingInvocation,
    stopAllPolling,
  };
}

